/** Matches `--color--occurrence-time-*` in `25live/styles/variables.scss` when CSS is not loaded (e.g. Karma). */
export const OccurrenceTimeColorFallbacks = {
    event: "#080",
    prePost: "#5cbf5c",
    setupTakedown: "#d2f4f6",
} as const;

export const OccurrenceTimeRangeLabels = {
    event: { start: "Start", end: "End" },
    prePost: { start: "Pre-Event", end: "Post-Event" },
    setupTakedown: { start: "Setup", end: "Takedown" },
} as const;

const cssVarByKey = {
    event: "--color--occurrence-time-event",
    prePost: "--color--occurrence-time-pre-post",
    setupTakedown: "--color--occurrence-time-setup-takedown",
} as const;

export type OccurrenceTimeColorKey = keyof typeof OccurrenceTimeColorFallbacks;

export type OccurrenceTimeColors = Record<OccurrenceTimeColorKey, string>;

export function occurrenceTimeBandForRange(
    startLabel: string | null | undefined,
    endLabel: string | null | undefined,
    rangeIndex: number,
): OccurrenceTimeColorKey {
    const start = startLabel?.trim() ?? "";
    const end = endLabel?.trim() ?? "";
    const { prePost, setupTakedown } = OccurrenceTimeRangeLabels;

    if (start === prePost.start || end === prePost.end) {
        return "prePost";
    }
    if (start === setupTakedown.start || start === "Set Up" || end === setupTakedown.end || end === "Take Down") {
        return "setupTakedown";
    }
    if (rangeIndex === 0) {
        return "event";
    }
    const legacyByIndex: { [key: number]: OccurrenceTimeColorKey } = {
        1: "prePost",
        2: "setupTakedown",
    };
    return legacyByIndex[rangeIndex] ?? "prePost";
}

export function readOccurrenceTimeColor(
    key: OccurrenceTimeColorKey,
    root: Element | null = typeof document !== "undefined" ? document.body : null,
): string {
    const fallback = OccurrenceTimeColorFallbacks[key];
    if (!root) {
        return fallback;
    }
    const value = getComputedStyle(root).getPropertyValue(cssVarByKey[key]).trim();
    return value || fallback;
}

export function readOccurrenceTimeColors(
    root: Element | null = typeof document !== "undefined" ? document.body : null,
): OccurrenceTimeColors {
    return {
        event: readOccurrenceTimeColor("event", root),
        prePost: readOccurrenceTimeColor("prePost", root),
        setupTakedown: readOccurrenceTimeColor("setupTakedown", root),
    };
}

export function readSliderRailOutsideColor(
    root: Element | null = typeof document !== "undefined" ? document.body : null,
): string {
    if (!root) {
        return "#fff";
    }
    return getComputedStyle(root).getPropertyValue("--color--surface-bg").trim() || "#fff";
}
